import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Fieldset, Input, Modal } from '@feathr/components';
import { useExportModal } from '@feathr/extender/hooks';
import { useStore } from '@feathr/extender/state';
import { flattenError, getDateRangeFromPreset } from '@feathr/hooks';

import type { ITransactionFilters } from '../TransactionTable/TransactionFilters.types';
import { donationTypeToBackend } from '../TransactionTable/TransactionFilters.utils';
import DataFieldsSelect from './DataFieldsSelect';

import * as styles from './ExportDonationsModal.css';

interface ISelectOption {
  id: string;
  name: string;
}

interface IProps {
  /**
   * Active filters from the TransactionTable filter drawer. The export uses
   * these for forms, date range, donation types, and donors — the modal only
   * collects the export-specific bits (email + which CSV fields to include).
   */
  filters: ITransactionFilters;
  opened: boolean;
  onClose: () => void;
}

function ExportDonationsModal({ filters, opened, onClose }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { DonationForms } = useStore();

  const {
    email,
    emailHadFocus,
    isLoading,
    isSubmitDisabled,
    validationErrors,
    handleEmailBlur,
    handleChangeEmail,
    handleExport,
    resetFormState,
  } = useExportModal(t);

  const [selectedOptionalFields, setSelectedOptionalFields] = useState<ISelectOption[]>([]);

  function handleSelectOptionalFields(values: ISelectOption[]): void {
    setSelectedOptionalFields(values);
  }

  function handleClose(): void {
    resetFormState();
    setSelectedOptionalFields([]);
    onClose();
  }

  async function handleConfirm(): Promise<void> {
    // getDateRangeFromPreset already returns {start: null, end: null} for 'all_time'.
    const drawerRange = getDateRangeFromPreset(filters.timeframe, filters.customDateRange);
    const donationTypes = filters.donationTypes.map(donationTypeToBackend);
    const perIds = filters.donors.map((d) => d.id);

    await handleExport(
      () =>
        DonationForms.exportDonations({
          email,
          start: drawerRange.start?.toISOString(),
          end: drawerRange.end?.toISOString(),
          form_ids: filters.formIds.length > 0 ? filters.formIds : undefined,
          donation_types: donationTypes.length > 0 ? donationTypes : undefined,
          per_ids: perIds.length > 0 ? perIds : undefined,
          fields: selectedOptionalFields.map((field) => field.id),
        }),
      handleClose,
      t,
    );
  }

  return (
    <Modal
      allowOverflow
      description={t(
        'Your CSV will reflect the filters you have applied to the transaction table. The file will be delivered via email with a secure download link since exporting may take a few minutes to process.',
      )}
      onClose={handleClose}
      opened={opened}
      rightActions={[
        <Button disabled={isLoading} key={'cancel'} onClick={handleClose}>
          {t('Cancel')}
        </Button>,
        <Button
          disabled={isSubmitDisabled}
          isLoading={isLoading}
          key={'confirm'}
          onClick={handleConfirm}
          type={'primary'}
        >
          {t('Send Export to Email')}
        </Button>,
      ]}
      title={t('Export donation data')}
    >
      <Fieldset className={styles.fieldset}>
        <Input
          label={t('Email address')}
          onBlur={handleEmailBlur}
          onChange={handleChangeEmail}
          placeholder={t('firstname.lastname@nonprofit.org')}
          type={'text'}
          validationError={emailHadFocus ? flattenError(validationErrors?.email) : undefined}
          value={email}
        />
        <DataFieldsSelect
          onSelectOptionalFields={handleSelectOptionalFields}
          selectedOptionalFields={selectedOptionalFields}
        />
      </Fieldset>
    </Modal>
  );
}

export default observer(ExportDonationsModal);
